import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Shield, 
  Badge, 
  Building, 
  Users, 
  ArrowRight, 
  CheckCircle, 
  Zap,
  Lock,
  Eye,
  Brain,
  Database,
  Globe,
  TrendingUp,
  AlertTriangle,
  Cpu,
  Wifi,
  Fingerprint,
  Satellite,
  Radar,
  Target,
  FileText,
  Scale,
  Gavel,
  Factory,
  Truck,
  MapPin,
  Clock,
  BarChart3,
  Network,
  Server,
  Key
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';

const Homepage = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState('operational');
  const { scrollY } = useScroll();
  
  const y1 = useTransform(scrollY, [0, 300], [0, 100]);
  const y2 = useTransform(scrollY, [0, 300], [0, -100]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.3]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const userTypeContent = {
    leo_military: {
      title: 'MILITARY-GRADE COMPLIANCE COMMAND CENTER',
      subtitle: 'Real-time threat detection • CJIS/FedRAMP certified • Zero-trust architecture',
      icon: Badge,
      color: 'from-red-600 via-red-700 to-red-900',
      accentColor: 'from-orange-400 to-red-500',
      features: [
        { name: 'CJIS Compliance', status: 'certified', icon: Shield },
        { name: 'FedRAMP Security', status: 'certified', icon: Lock },
        { name: 'Real-time Monitoring', status: 'active', icon: Radar },
        { name: 'Threat Intelligence', status: 'active', icon: Target },
        { name: 'Chain of Custody', status: 'active', icon: Fingerprint }
      ],
      quickActions: [
        { name: 'Threat Assessment', path: '/compliance', icon: Target, color: 'from-red-500 to-red-700' },
        { name: 'Real-time Monitoring', path: '/iot-devices', icon: Radar, color: 'from-orange-500 to-orange-700' },
        { name: 'Security Dashboard', path: '/dashboard', icon: Shield, color: 'from-red-600 to-red-800' }
      ]
    },
    bail_bonds: {
      title: 'COURT-INTEGRATED COMPLIANCE HUB',
      subtitle: 'ECPA certified • Privacy-first design • Real-time court integration',
      icon: Building,
      color: 'from-purple-600 via-purple-700 to-purple-900',
      accentColor: 'from-purple-400 to-pink-500',
      features: [
        { name: 'ECPA Compliance', status: 'certified', icon: Scale },
        { name: 'Privacy Rights', status: 'certified', icon: Eye },
        { name: 'Court Integration', status: 'active', icon: Gavel },
        { name: 'Bail Monitoring', status: 'active', icon: Clock },
        { name: 'Audit Trail', status: 'active', icon: FileText }
      ],
      quickActions: [
        { name: 'Bail Bond Status', path: '/compliance', icon: Scale, color: 'from-purple-500 to-purple-700' },
        { name: 'Court Integration', path: '/compliance-dashboard', icon: Gavel, color: 'from-pink-500 to-pink-700' },
        { name: 'Privacy Dashboard', path: '/dashboard', icon: Eye, color: 'from-purple-600 to-purple-800' }
      ]
    },
    ffl_manufacturer: {
      title: 'INDUSTRY-LEADING COMPLIANCE PLATFORM',
      subtitle: 'ATF certified • UL 2900 compliant • Automated reporting',
      icon: Users,
      color: 'from-blue-600 via-blue-700 to-blue-900',
      accentColor: 'from-blue-400 to-cyan-500',
      features: [
        { name: 'ATF Compliance', status: 'certified', icon: Shield },
        { name: 'UL 2900 Security', status: 'certified', icon: Lock },
        { name: 'Inventory Management', status: 'active', icon: Database },
        { name: 'Automated Reporting', status: 'active', icon: FileText },
        { name: 'Quality Control', status: 'active', icon: CheckCircle }
      ],
      quickActions: [
        { name: 'ATF Compliance', path: '/atf-compliance', icon: Shield, color: 'from-blue-500 to-blue-700' },
        { name: 'Inventory Management', path: '/inventory', icon: Database, color: 'from-cyan-500 to-cyan-700' },
        { name: 'Compliance Dashboard', path: '/compliance-dashboard', icon: BarChart3, color: 'from-blue-600 to-blue-800' }
      ]
    }
  };

  const currentContent = userTypeContent[user?.user_type] || userTypeContent.ffl_manufacturer;
  const IconComponent = currentContent.icon;

  const systemMetrics = [
    { label: 'System Status', value: systemStatus, icon: Server, color: 'text-green-400' },
    { label: 'Active Users', value: '2,847', icon: Users, color: 'text-blue-400' },
    { label: 'Compliance Score', value: '99.7%', icon: CheckCircle, color: 'text-green-400' },
    { label: 'Threat Level', value: 'LOW', icon: AlertTriangle, color: 'text-yellow-400' }
  ];

  const complianceBadges = [
    { name: 'CJIS', status: 'certified', color: 'bg-green-500' },
    { name: 'FedRAMP', status: 'certified', color: 'bg-blue-500' },
    { name: 'ATF', status: 'certified', color: 'bg-purple-500' },
    { name: 'UL 2900', status: 'certified', color: 'bg-orange-500' },
    { name: 'ECPA', status: 'certified', color: 'bg-pink-500' },
    { name: 'ISO 27001', status: 'certified', color: 'bg-indigo-500' }
  ];

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-red-900/20" />
        <div className="absolute top-0 left-0 w-full h-full">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400 rounded-full"
              animate={{
                x: [0, 100, 0],
                y: [0, 100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Hero Section with Parallax */}
      <div className="relative min-h-screen flex items-center justify-center">
        <motion.div style={{ y: y1, opacity }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        </motion.div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8">
          {/* Logo and Brand */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
            className="mb-8"
          >
            <div className={`w-32 h-32 bg-gradient-to-r ${currentContent.color} rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl`}>
              <IconComponent className="w-16 h-16 text-white" />
            </div>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
              <span className="text-green-400 text-sm font-mono">SYSTEM ACTIVE</span>
            </div>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight"
          >
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {currentContent.title.split(' ').slice(0, -2).join(' ')}
            </span>
            <br />
            <span className={`bg-gradient-to-r ${currentContent.accentColor} bg-clip-text text-transparent`}>
              {currentContent.title.split(' ').slice(-2).join(' ')}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto font-light"
          >
            {currentContent.subtitle}
          </motion.p>

          {/* System Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12"
          >
            {systemMetrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4">
                  <metric.icon className={`w-8 h-8 ${metric.color} mx-auto mb-2`} />
                  <div className="text-2xl font-bold text-white mb-1">{metric.value}</div>
                  <div className="text-sm text-gray-400">{metric.label}</div>
                </div>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons - Only show when not logged in */}
          {!user && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link
                to="/login"
                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
              >
                <span className="flex items-center">
                  Log In
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
              
            </motion.div>
          )}
        </div>

        {/* Floating Elements */}
        <motion.div
          style={{ y: y2 }}
          className="absolute top-20 right-20 hidden lg:block"
        >
          <div className="w-32 h-32 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl" />
        </motion.div>
        
        <motion.div
          style={{ y: y1 }}
          className="absolute bottom-20 left-20 hidden lg:block"
        >
          <div className="w-40 h-40 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl" />
        </motion.div>
      </div>

      {/* Features Grid */}
      <div className="relative py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Advanced <span className={`bg-gradient-to-r ${currentContent.accentColor} bg-clip-text text-transparent`}>Compliance</span> Features
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Enterprise-grade security and compliance automation powered by AI
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {currentContent.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl">
                  <div className="flex items-center mb-6">
                    <div className={`w-12 h-12 bg-gradient-to-r ${currentContent.color} rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{feature.name}</h3>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${feature.status === 'certified' ? 'bg-green-400' : 'bg-blue-400'}`} />
                        <span className="text-sm text-gray-400 capitalize">{feature.status}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Enterprise-grade {feature.name.toLowerCase()} with real-time monitoring and automated compliance checks.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Compliance Badges */}
      <div className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Industry <span className="bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">Certifications</span>
            </h2>
            <p className="text-xl text-gray-400">
              Trusted by government agencies and industry leaders
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {complianceBadges.map((badge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className={`w-20 h-20 ${badge.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-white">{badge.name}</h3>
                <p className="text-xs text-green-400 font-mono">CERTIFIED</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Quick <span className={`bg-gradient-to-r ${currentContent.accentColor} bg-clip-text text-transparent`}>Actions</span>
            </h2>
            <p className="text-xl text-gray-400">
              Access your most important tools and features
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {currentContent.quickActions.map((action, index) => {
              const ActionIcon = action.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={action.path}
                    className="block group"
                  >
                    <div className="bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl">
                      <div className="text-center">
                        <div className={`w-20 h-20 bg-gradient-to-r ${action.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                          <ActionIcon className="w-10 h-10 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-4">{action.name}</h3>
                        <div className="flex items-center justify-center text-blue-400 group-hover:text-blue-300 transition-colors">
                          <span className="mr-2 font-semibold">Access Now</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Welcome Section */}
      {user && (
        <div className="py-20 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gray-800/80 backdrop-blur-sm p-12 rounded-3xl border border-gray-700 shadow-2xl">
                <div className={`w-24 h-24 bg-gradient-to-r ${currentContent.color} rounded-3xl flex items-center justify-center mx-auto mb-8`}>
                  <IconComponent className="w-12 h-12 text-white" />
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-6">
                  Welcome back, <span className="text-blue-400">{user.username}</span>!
                </h3>
                
                <p className="text-xl text-gray-300 mb-8">
                  You're logged in as a <span className={`bg-gradient-to-r ${currentContent.accentColor} bg-clip-text text-transparent font-bold`}>
                    {user.user_type_name}
                  </span> user
                  {user.organization && ` from ${user.organization}`}.
                </p>
                
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                  {user.features?.map((feature, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 text-blue-300 rounded-full text-sm font-medium"
                    >
                      {feature.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/dashboard"
                    className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105"
                  >
                    Go to Dashboard
                  </Link>
                  <button className="px-8 py-4 border-2 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-blue-400 font-bold rounded-xl transition-all duration-300">
                    View Profile
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="py-12 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm font-mono">
              {currentTime.toLocaleTimeString()} • SYSTEM OPERATIONAL
            </span>
          </div>
          <p className="text-gray-400">
            © 2024 Dark Matter Solutions. Enterprise-grade compliance and security platform.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
